import { CSSProperties } from 'react';
import { AiChatVariant } from './types';

/**
 * AiChatView 테마 토큰 — CSS 커스텀 프로퍼티로 Root 에 주입한다.
 * emotion/MUI 가 전역 ThemeProvider 를 공유하므로 ThemeProvider 대신
 * 파일 뷰어가 이미 쓰던 CSS 변수 패턴을 채택했다.
 * styles.ts / Markdown.tsx 는 모두 var(--aichat-*) 를 참조한다.
 */
// 라이트 값은 var(--dk-<토큰>, <라이트 값>) — 앱 웹뷰 다크 모드에서는 앱 다크 팔레트를 따른다 (styles/appDarkMode.ts)
const LIGHT: Record<string, string> = {
  '--aichat-bg': 'var(--dk-bg-subtle, #F9FAFB)',
  '--aichat-bg-mobile': 'var(--dk-bg, #ffffff)',
  '--aichat-text': 'var(--dk-text, #000000)',
  '--aichat-text-sub': 'var(--dk-text-secondary, #888D96)',
  '--aichat-text-muted': 'var(--dk-text-dim, #B7BBC2)',
  '--aichat-border': 'var(--dk-line, #E7E9EC)',
  '--aichat-divider': 'var(--dk-line, #E7E9EC)',
  '--aichat-header-divider': 'var(--dk-line-strong, #D4D7DB)',
  '--aichat-hover': 'var(--dk-bg-muted, #F3F5F6)',
  '--aichat-active': 'var(--dk-bg-strong, #E7E9EC)',
  '--aichat-user-bubble': 'var(--dk-line-strong, #34383d)',
  '--aichat-user-text': '#ffffff',
  '--aichat-user-border': 'var(--dk-line-strong, #000000)',
  '--aichat-input-bg': 'var(--dk-bg, #ffffff)',
  '--aichat-input-bg-disabled': 'var(--dk-bg-subtle, #F9FAFB)',
  '--aichat-input-focus': 'var(--dk-text, #000000)',
  '--aichat-input-disabled-text': 'var(--dk-text-disabled, #D1D8E2)',
  '--aichat-chip-hover': 'var(--dk-bg-muted, #F9FAFB)',
  '--aichat-link': 'var(--dk-accent, #0066CC)',
  '--aichat-link-disabled': 'var(--dk-text-disabled, #D1D8E2)',
  '--aichat-send-active': 'var(--dk-inverse-bg, #000000)',
  '--aichat-send-inactive': 'var(--dk-bg-strong, #D1D8E2)',
  '--aichat-send-pressed': 'var(--dk-accent, #0066CC)',
  '--aichat-shadow': 'rgba(0, 0, 0, 0.08)',
  '--aichat-footer-from': 'var(--dk-bg-subtle-clear, rgba(249, 250, 251, 0))',
  '--aichat-footer-to': 'var(--dk-bg-subtle, #F9FAFB)',
  '--aichat-footer-from-mobile': 'var(--dk-bg-clear, rgba(255, 255, 255, 0))',
  '--aichat-footer-to-mobile': 'var(--dk-bg, #ffffff)',
  // markdown
  '--aichat-md-link': '#6a5af9',
  '--aichat-md-quote-border': 'var(--dk-line-strong, #d1d9e0)',
  '--aichat-md-quote-text': 'var(--dk-text-strong, #59636e)',
  '--aichat-md-hr': 'var(--dk-line-strong, #d1d9e0)',
  '--aichat-md-code-bg': 'rgba(129, 139, 152, 0.12)',
  '--aichat-md-pre-bg': 'var(--dk-bg-subtle, #f6f8fa)',
  '--aichat-md-table-border': 'var(--dk-line-strong, #d1d9e0)',
  '--aichat-md-th-bg': 'var(--dk-bg-subtle, #f6f8fa)',
};

// 다크 값은 기존 AI File Viewer 팔레트(FileViewerAIPanel) 를 그대로 사용.
const DARK: Record<string, string> = {
  '--aichat-bg': '#2B2D2F',
  '--aichat-bg-mobile': '#2B2D2F',
  '--aichat-text': '#FFFFFF',
  '--aichat-text-sub': '#888D96',
  '--aichat-text-muted': '#676C74',
  '--aichat-border': '#393D41',
  '--aichat-divider': '#9FA5AD',
  '--aichat-header-divider': '#505560',
  '--aichat-hover': '#484D52',
  '--aichat-active': '#393D41',
  // 질문 말풍선 — 기존 파일 뷰어와 동일하게 회색 배경 + 흰 테두리 + 어두운 텍스트
  '--aichat-user-bubble': '#BBC0C7',
  '--aichat-user-text': '#2B2D2F',
  '--aichat-user-border': '#FFFFFF',
  // 입력창·추천칩 배경은 시트와 같은 gray_White(#2B2D2F)로 두고
  // gray_3(#393D41) 보더로 외곽선을 드러낸다(Figma AI Viewer 기준).
  '--aichat-input-bg': '#2B2D2F',
  '--aichat-input-bg-disabled': '#2B2D2F',
  '--aichat-input-focus': '#FFFFFF',
  '--aichat-input-disabled-text': '#464C53',
  '--aichat-chip-hover': '#484D52',
  '--aichat-link': '#67BDFF',
  '--aichat-link-disabled': '#60666D',
  '--aichat-send-active': '#C77AC4',
  '--aichat-send-inactive': '#464C53',
  '--aichat-send-pressed': '#A85AA5',
  // 다크: 입력창 wrapper 그림자(0 0 16px)는 대비가 과해 적용하지 않는다(투명).
  '--aichat-shadow': 'transparent',
  '--aichat-footer-from': 'rgba(43, 45, 47, 0)',
  '--aichat-footer-to': '#2B2D2F',
  '--aichat-footer-from-mobile': 'rgba(43, 45, 47, 0)',
  '--aichat-footer-to-mobile': '#2B2D2F',
  '--aichat-md-link': '#C77AC4',
  '--aichat-md-quote-border': '#505560',
  '--aichat-md-quote-text': '#B7BBC2',
  '--aichat-md-hr': '#505560',
  '--aichat-md-code-bg': 'rgba(255, 255, 255, 0.12)',
  '--aichat-md-pre-bg': '#1F2327',
  '--aichat-md-table-border': '#505560',
  '--aichat-md-th-bg': '#393D41',
};

export const getAiChatVars = (variant: AiChatVariant): CSSProperties =>
  (variant === 'dark' ? DARK : LIGHT) as unknown as CSSProperties;
